import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, User } from "lucide-react";

const Login = ({ setAuth }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    console.log("🔧 API URL configured as:", API);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log("🔐 Attempting login to:", `${API}/auth/login`);
      const response = await axios.post(
        `${API}/auth/login`,
        {
          username,
          password,
        },
        {
          timeout: 10000, // 10 second timeout
        }
      );

      console.log("✅ Login response received:", response.data);
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      toast.success("Login successful!");
      setAuth(true);
      navigate("/");
    } catch (error) {
      console.error("❌ Login error:", error);
      console.error("Error response:", error.response);
      console.error("Error message:", error.message);
      
      let errorMsg = "Login failed";
      if (error.code === "ECONNABORTED") {
        errorMsg = "Connection timeout - Please check your network";
      } else if (error.response) {
        errorMsg = error.response.data?.detail || `Error: ${error.response.status}`;
      } else if (error.request) {
        errorMsg = "No response from server - Please check backend URL";
      } else {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
    } finally {
      console.log("🏁 Login attempt finished");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <div className="w-full max-w-md p-8 bg-white rounded-2xl shadow-xl">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_98f43d7c-462e-47e8-bf72-c54d3fbfbeeb/artifacts/kkp6xmyc_bano_fresh_logo.png" 
            alt="Bano Fresh Logo" 
            className="h-24 w-24 mx-auto mb-4 object-contain"
          />
          <h1 className="text-4xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Bano Fresh
          </h1>
          <p className="text-gray-600">Sign in to your account</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <div className="relative">
              <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <Input
                id="username"
                type="text"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="pl-10"
                data-testid="login-username-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <Input
                id="password"
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="pl-10"
                data-testid="login-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            disabled={loading}
            data-testid="login-submit-button"
          >
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-500">
          Contact administrator for account access
        </div>
      </div>
    </div>
  );
};

export default Login;
